import { registerToken } from '@rsos/maps/providers/esri/identityManager/registerToken';
import { instance } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';
import { PSAPInfo } from '../capstone/psapInfo/psapInfoSlice';
import { gisServiceSort } from './gisServiceSort';
import { GISIdentity, GISServiceTypes } from './gisServicesTypes';
import { getGISLayerID } from './layer-loaders/getGISLayerID';
import { loadLayers } from './layer-loaders/loadLayers';

type FetchGISServicesResponse = {
  gis_identities: GISIdentity[];
};

export const getGISServices = (orgName: string) =>
  instance.get<FetchGISServicesResponse>(
    `${getAPIHost()}/v1/capstone/psaps/${orgName}/gis-services`,
    {
      headers: { retry: true },
    },
  );

export const getLoadedGISServices = async (
  orgName: string,
  settings: PSAPInfo['map_layer_settings'] = {},
  shouldLoadLayers = true,
) => {
  const response = await getGISServices(orgName);
  const data = response.data as FetchGISServicesResponse;

  // PSAPs can choose to enable layers by default (see
  // /admin/data-source/map-layers). If a layer should be on by default, set
  // its auto_checked here, and it will be turned on after the layers are
  // loaded into state.
  data.gis_identities.forEach(gisID => {
    gisID.gis_services.forEach(service => {
      if (settings[service.id]) {
        service.auto_checked ||= settings[service.id].default_checked;
      }
      service.gisLayerID = getGISLayerID(service);
    });
  });

  const gisIdentities = mapGISIdentities(data.gis_identities || []);

  const services = mapGISServices(data.gis_identities || []);

  const basemapService = services.find(
    service => service.service_type === GISServiceTypes.basemap,
  );
  if (basemapService) {
    registerToken(
      basemapService.gis_service_url,
      basemapService.token as string,
    );
  }
  const layerMap = shouldLoadLayers ? await loadLayers(services) : [];
  return { gisIdentities, services, layerMap };
};

/**
 * Map the GIS identities from the `/gis-services` payload
 */
function mapGISIdentities(gisIdentities: GISIdentity[]) {
  return gisIdentities.map(identity => {
    const {
      arcgis_token: arcGISToken,
      gis_identity_url: gisIdentityUrl,
      gis_services: gisServices,
      token_expires_in: tokenExpiresIn,
    } = identity;

    return {
      arcGISToken,
      gisServices,
      gisIdentityUrl,
      tokenExpiresIn,
    };
  });
}
/**
 * Map the GIS services to a flat sorted array based on the display_order property from the
 * /gis-services payload.
 */
function mapGISServices(gisIdentities: GISIdentity[]) {
  let gisServices = [];
  let layerIndex = 1;

  gisServices = gisIdentities
    .map(identity => {
      return identity.gis_services.map(service => {
        return {
          ...service,
          // Ensure attributes is never null (API may return null for some services)
          attributes: service.attributes || {},
          token: identity?.arcgis_token || undefined,
        };
      });
    })
    .flat()
    .sort(gisServiceSort)
    .map(service => {
      // Add a new layerIndex property to maintain this exact order.
      return {
        ...service,
        layerIndex: layerIndex++,
      };
    });

  return gisServices;
}
